import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, X, ChevronDown, User, Plus, Search, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const ChatWidget = ({ userId, userName, userType = 'freelancer' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  
  // New conversation state
  const [showNewChat, setShowNewChat] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Fetch available contacts for new conversation
  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      // For freelancers: show admin contacts
      // For admins: show all freelancers
      const endpoint = userType === 'admin' 
        ? `${API_URL}/api/freelancer-users`
        : `${API_URL}/api/messages/admin-contacts`;
      
      const res = await fetch(endpoint);
      if (res.ok) {
        const data = await res.json();
        // Filter out self
        const filtered = data.filter(c => c.id !== userId && c.email !== userId);
        setContacts(filtered);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // Fallback: set empty or default admin contact
      if (userType === 'freelancer') {
        setContacts([{ id: 'admin', name: 'RevoCut Support', email: 'info@revocut-media.de' }]);
      }
    } finally {
      setLoadingContacts(false);
    }
  };

  // Start new conversation
  const startNewConversation = (contact) => {
    setSelectedConversation({
      conversation_id: contact.id || contact.email,
      other_user: {
        id: contact.id || contact.email,
        name: contact.name,
        email: contact.email
      },
      last_message: null,
      unread_count: 0
    });
    setShowNewChat(false);
    setMessages([]);
  };

  // Fetch conversations
  const fetchConversations = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/messages/conversations/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
        
        // Calculate total unread
        const total = data.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setUnreadCount(total);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  // Fetch messages for selected conversation
  const fetchMessages = async (otherUserId) => {
    if (!userId || !otherUserId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/${userId}/${otherUserId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        // Refresh conversations to update unread count
        fetchConversations();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: userId,
          sender_name: userName,
          receiver_id: selectedConversation.conversation_id,
          receiver_name: selectedConversation.other_user.name,
          receiver_email: selectedConversation.other_user.email,
          text: newMessage
        })
      });
      
      if (res.ok) {
        setNewMessage('');
        fetchMessages(selectedConversation.conversation_id);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial fetch
  useEffect(() => {
    if (userId) {
      fetchConversations();
      // Poll for new messages every 30 seconds
      const interval = setInterval(fetchConversations, 30000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  // Fetch messages when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.conversation_id);
    }
  }, [selectedConversation]);

  if (!userId) return null;

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-50 transition-all hover:scale-110"
        style={{ backgroundColor: '#5C63FF' }}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <MessageSquare className="w-6 h-6 text-white" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 w-96 h-[500px] rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-white/10"
          style={{ backgroundColor: '#1a1a1a' }}
        >
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between" style={{ backgroundColor: '#5C63FF' }}>
            <div className="flex items-center gap-2">
              {(selectedConversation || showNewChat) && (
                <button 
                  onClick={() => { setSelectedConversation(null); setShowNewChat(false); }}
                  className="text-white/80 hover:text-white mr-1"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <MessageSquare className="w-5 h-5 text-white" />
              <span className="font-bold text-white">
                {showNewChat ? 'Neue Nachricht' : selectedConversation ? selectedConversation.other_user.name : 'Nachrichten'}
              </span>
            </div>
            {!selectedConversation && !showNewChat && (
              <button 
                onClick={() => { setShowNewChat(true); fetchContacts(); }}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
                title="Neue Nachricht"
              >
                <Plus className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {showNewChat ? (
              // New Chat - Contact Selection
              <div className="h-full flex flex-col">
                <div className="p-3 border-b border-white/10">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Kontakt suchen..."
                      className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {loadingContacts ? (
                    <div className="text-center py-8">
                      <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="p-6 text-center text-gray-400">
                      <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Keine Kontakte verfügbar</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {contacts
                        .filter(c => 
                          !contactSearch || 
                          c.name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                          c.email?.toLowerCase().includes(contactSearch.toLowerCase())
                        )
                        .map((contact) => (
                          <button
                            key={contact.id || contact.email}
                            onClick={() => startNewConversation(contact)}
                            className="w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
                          >
                            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)' }}>
                              <User className="w-5 h-5" style={{ color: '#5C63FF' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white truncate">{contact.name}</p>
                              <p className="text-sm text-gray-400 truncate">{contact.email}</p>
                            </div>
                          </button>
                        ))
                      }
                    </div>
                  )}
                </div>
              </div>
            ) : !selectedConversation ? (
              // Conversation List
              <div className="h-full overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="p-6 text-center text-gray-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Nachrichten</p>
                    <p className="text-sm mt-1">Hier erscheinen deine Unterhaltungen</p>
                    <button
                      onClick={() => { setShowNewChat(true); fetchContacts(); }}
                      className="mt-4 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                      style={{ backgroundColor: '#5C63FF' }}
                    >
                      <Plus className="w-4 h-4 inline mr-1" />
                      Neue Nachricht
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {conversations.map((conv) => (
                      <button
                        key={conv.conversation_id}
                        onClick={() => setSelectedConversation(conv)}
                        className="w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)' }}>
                          <User className="w-5 h-5" style={{ color: '#5C63FF' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-white truncate">{conv.other_user.name}</p>
                            {conv.unread_count > 0 && (
                              <span className="w-5 h-5 bg-purple-500 rounded-full text-xs text-white flex items-center justify-center">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-400 truncate">{conv.last_message?.text}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Messages View
              <div className="h-full flex flex-col">
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p>Noch keine Nachrichten</p>
                      <p className="text-sm">Schreibe die erste Nachricht!</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isOwn = msg.sender_id === userId;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                              isOwn 
                                ? 'bg-purple-500 text-white rounded-br-md' 
                                : 'bg-white/10 text-white rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm">{msg.text}</p>
                            <p className={`text-xs mt-1 ${isOwn ? 'text-white/60' : 'text-gray-500'}`}>
                              {new Date(msg.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Nachricht schreiben..."
                      className="flex-1 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-50"
                      style={{ backgroundColor: '#5C63FF' }}
                    >
                      <Send className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
